import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PAGE_SCHEMA } from './page-schema';

@Injectable({
  providedIn: 'root'
})
export class SeoServiceTsService {

  private canonicalLink: HTMLLinkElement | any;

  constructor(@Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private titleService: Title,
    private metaService: Meta,
    @Optional() @Inject(PAGE_SCHEMA) private pageSchema: ((url: string) => any[]) | null,
  ) {
    // Create canonical tag once
    this.createOrGetCanonicalTag();

    // Automatically update canonical URL on route change
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        const canonicalUrl = this.document.location.origin + event.urlAfterRedirects;
        this.setCanonicalUrl(canonicalUrl);
      });

    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
        .subscribe(() => this.clearPageSchema());
    }

  }


  private createOrGetCanonicalTag(): HTMLLinkElement {
    let link: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']");

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    this.canonicalLink = link;
    return link;
  }

  public setCanonicalUrl(url: any): void {
    if (this.canonicalLink) {
      this.canonicalLink.setAttribute('href', url);
    }
  }

  public removeCanonicalTag(): void {
    if (this.canonicalLink && this.document.head.contains(this.canonicalLink)) {
      this.document.head.removeChild(this.canonicalLink);
    }
  }

  public setLinkTag(rel: any, href: any): void {
    let link: HTMLLinkElement | null = this.document.querySelector(`link[rel='${rel}']`);

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', rel);
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', href);
  }

  updateCanonicalUrl(url: string): void {
    const head = this.document.getElementsByTagName('head')[0];
    let link: HTMLLinkElement | null = this.document.querySelector(`link[rel='canonical']`);

    if (!link) {
      link = this.document.createElement('link') as HTMLLinkElement;
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }

    console.log(url)
    link.setAttribute('href', url);
  }

  // setCanonicalURL(url?: string): void {
  //   if (isPlatformBrowser(this.platformId)) {
  //     const canonicalUrl = url || window.location.origin + this.router.url; // Generate full URL

  //     // Remove existing canonical tag if it exists
  //     let link: HTMLLinkElement | null = document.querySelector("link[rel='canonical']");
  //     if (link) {
  //       link.setAttribute('href', canonicalUrl);
  //     } else {
  //       // Create a new canonical tag if it doesn’t exist
  //       link = document.createElement('link');
  //       link.setAttribute('rel', 'canonical');
  //       link.setAttribute('href', canonicalUrl);
  //       document.head.appendChild(link);
  //     }
  //   }
  // }

  setCanonicalURL(url?: string): void {
    let baseUrl: string;

    if (isPlatformBrowser(this.platformId)) {
      baseUrl = window.location.origin;
    } else {
      baseUrl = 'https://www.atmavaan.com';
    }

    const canonicalUrl = url || baseUrl + this.router.url;

    if (this.document) {
      let link: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']");
      if (link) {
        link.setAttribute('href', canonicalUrl);
      } else {
        link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonicalUrl);
        this.document.head.appendChild(link);
      }
    }
  }

  setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  setMetaDescription(description: string): void {
    const existingMeta = this.metaService.getTag("name='description'");
    if (existingMeta) {
      this.metaService.updateTag({ name: 'description', content: description });
    } else {
      this.metaService.addTag({ name: 'description', content: description });
    }
  }

  updateSeoData(title: string, description: string, url?: string): void {
    this.setTitle(title);
    this.setMetaDescription(description);
    this.setCanonicalURL(url);
  }

  setPageSchema(schemas: any[]): void {
    this.clearPageSchema();
    schemas.forEach((schema) => {
      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-schema', 'true');
      script.text = JSON.stringify(schema);
      this.document.head.appendChild(script);
    });
  }

  clearPageSchema(): void {
    const existing = this.document.head.querySelectorAll('script[data-page-schema]');
    existing.forEach((el) => el.parentNode?.removeChild(el));
  }

  addSchemaForUrl(url: string): void {

    const cleanUrl = url.split('#')[0];
    const schemas = this.getSchemaForUrl(cleanUrl);

    if (isPlatformServer(this.platformId)) {
      schemas.forEach((schema) => {
        const script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(schema);
        this.document.head.appendChild(script);
      });
    }
  }

  // Only the server has PAGE_SCHEMA (see app.module.server.ts); in the browser the JSON-LD is already in the page.
  getSchemaForUrl(url: string): any[] {
    return this.pageSchema ? this.pageSchema(url) : [];
  }

  isServer(): boolean {
    return isPlatformServer(this.platformId);
  }

}